import { NumberInput, Stack, TextInput } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { forwardRef, useEffect, useImperativeHandle } from "react"
import { useTranslation } from "react-i18next"
import { useCreateTag, useUpdateTag } from "../hooks/useTags"
import type { Tag, TagFormValues } from "../models/tag"
import { TagFormSchema } from "../models/tag"

interface Props {
  tag?: Tag
  onSuccess: () => void
}

const TagFormContent = forwardRef<{ submit: () => void }, Props>(
  ({ tag, onSuccess }, ref) => {
    const { t } = useTranslation()
    const createMutation = useCreateTag()
    const updateMutation = useUpdateTag()
    const isEdit = !!tag

    const form = useForm<TagFormValues>({
      initialValues: { name: "", order: 0 },
      validate: (values) => {
        const result = TagFormSchema.safeParse(values)
        if (!result.success) {
          return Object.fromEntries(
            result.error.issues.map((e) => [e.path[0], e.message]),
          )
        }
        return {}
      },
    })

    useEffect(() => {
      if (tag) {
        form.setValues({ name: tag.name, order: tag.order })
      }
    }, [tag])

    const handleSubmit = (values: TagFormValues) => {
      if (isEdit && tag) {
        updateMutation.mutate(
          { id: tag.id, values },
          {
            onSuccess: () => {
              notifications.show({
                color: "green",
                title: t("common.success"),
                message: t("tags.updateSuccess"),
              })
              onSuccess()
            },
            onError: () => {
              notifications.show({
                color: "red",
                title: t("common.error"),
                message: t("common.error"),
              })
            },
          },
        )
      } else {
        createMutation.mutate(values, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("tags.createSuccess"),
            })
            onSuccess()
          },
          onError: () => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: t("common.error"),
            })
          },
        })
      }
    }

    const handleFormSubmit = form.onSubmit(handleSubmit)
    useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }), [
      handleFormSubmit,
    ])

    return (
      <form onSubmit={handleFormSubmit}>
        <Stack p="md">
          <TextInput
            label={t("tags.name")}
            placeholder={t("tags.name")}
            withAsterisk
            {...form.getInputProps("name")}
          />
          <NumberInput
            label={t("tags.order")}
            placeholder={t("tags.order")}
            min={0}
            {...form.getInputProps("order")}
          />
        </Stack>
      </form>
    )
  },
)

export default TagFormContent
